import { onUnmounted, ref, Ref } from 'vue';

/**
 * Visibility Manager - управление видимостью компонента через IntersectionObserver
 * 
 * Requirements:
 * - 7.1: IntersectionObserver с root margin 50px
 * - 7.2: Приостановка анимаций когда меню невидимо
 * - 7.3: Возобновление анимаций при появлении в viewport
 * - 7.4: Отключение observer при unmount
 * - 7.5: Graceful fallback если IntersectionObserver не поддерживается
 * - 19.3: Приостановка background animations при невидимости
 * - 21.3: Fallback если IntersectionObserver не поддерживается
 */

export interface VisibilityManagerOptions {
  /**
   * Root margin для IntersectionObserver (default: '50px')
   * Позволяет начать подготовку до появления элемента в viewport
   */
  rootMargin?: string;
  
  /**
   * Threshold для IntersectionObserver (default: 0)
   * 0 = срабатывает как только хотя бы 1px элемента виден
   */
  threshold?: number | number[];
  
  /**
   * Callback при изменении видимости
   */
  onVisibilityChange?: (isVisible: boolean) => void;
  
  /**
   * Включить логирование для отладки
   */
  enableLogging?: boolean;
}

export interface VisibilityManager {
  /**
   * Текущее состояние видимости
   */
  isVisible: Ref<boolean>;
  
  /**
   * Инициализировать observer для элемента
   */
  observe: (element: HTMLElement) => void;
  
  /**
   * Остановить наблюдение
   */
  disconnect: () => void;
  
  /**
   * Приостановить все анимации
   */
  pauseAnimations: () => void;
  
  /**
   * Возобновить все анимации
   */
  resumeAnimations: () => void;
  
  /**
   * Проверка поддержки IntersectionObserver
   */
  isSupported: boolean;
  
  /**
   * Cleanup всех ресурсов
   */
  cleanup: () => void;
}

/**
 * Composable для управления видимостью компонента
 * 
 * @param options - Опции конфигурации
 * @returns VisibilityManager interface
 */
export function useVisibilityManager(
  options: VisibilityManagerOptions = {}
): VisibilityManager {
  const {
    rootMargin = '50px',
    threshold = 0,
    onVisibilityChange,
    enableLogging = false
  } = options;
  
  // State
  const isVisible = ref(true); // По умолчанию видим (для fallback)
  let observer: IntersectionObserver | null = null;
  let observedElement: HTMLElement | null = null;
  const pausedAnimations = new Set<Animation>();
  
  // Проверка поддержки IntersectionObserver (Requirement 7.5, 21.3)
  const isSupported = !process.env.SERVER && typeof window !== 'undefined' && 'IntersectionObserver' in window;
  
  /**
   * Логирование (только если включено)
   */
  const log = (message: string, ...args: any[]) => {
    if (enableLogging && !process.env.SERVER) {
      console.log(`[VisibilityManager] ${message}`, ...args);
    }
  };
  
  /**
   * Приостановить все CSS анимации элемента
   * Requirement 7.2, 19.3
   */
  const pauseAnimations = () => {
    if (process.env.SERVER || !observedElement) return;
    
    try {
      // Получаем все активные CSS анимации
      const animations = observedElement.getAnimations({ subtree: true });
      
      animations.forEach(animation => {
        if (animation.playState === 'running') {
          animation.pause();
          pausedAnimations.add(animation);
        }
      });
      
      log('Paused animations', pausedAnimations.size);
    } catch (error) {
      log('Error pausing animations:', error);
    }
  };
  
  /**
   * Возобновить все приостановленные анимации
   * Requirement 7.3
   */
  const resumeAnimations = () => {
    if (process.env.SERVER || !observedElement) return;
    
    try {
      pausedAnimations.forEach(animation => {
        if (animation.playState === 'paused') {
          animation.play();
        }
      });
      
      pausedAnimations.clear();
      log('Resumed animations');
    } catch (error) {
      log('Error resuming animations:', error);
    }
  };
  
  /**
   * Обработчик изменения видимости
   */
  const handleIntersection = (entries: IntersectionObserverEntry[]) => {
    entries.forEach(entry => {
      const wasVisible = isVisible.value;
      isVisible.value = entry.isIntersecting;
      
      log('Visibility changed:', isVisible.value);
      
      // Вызываем callback если видимость изменилась
      if (wasVisible !== isVisible.value) {
        onVisibilityChange?.(isVisible.value);
        
        // Управляем анимациями (Requirement 7.2, 7.3, 19.3)
        if (isVisible.value) {
          resumeAnimations();
        } else {
          pauseAnimations();
        }
      }
    });
  };
  
  /**
   * Начать наблюдение за элементом
   * Requirement 7.1
   */
  const observe = (element: HTMLElement) => {
    // Graceful fallback если IntersectionObserver не поддерживается (Requirement 7.5, 21.3)
    if (!isSupported) {
      log('IntersectionObserver not supported, assuming always visible');
      isVisible.value = true;
      return;
    }
    
    // Отключаем предыдущий observer если есть
    disconnect();
    
    observedElement = element;
    
    try {
      // Создаём IntersectionObserver с заданными опциями (Requirement 7.1)
      observer = new IntersectionObserver(handleIntersection, {
        rootMargin,
        threshold
      });
      
      observer.observe(element);
      log('Started observing element', { rootMargin, threshold });
    } catch (error) {
      log('Error creating IntersectionObserver:', error);
      // Fallback: считаем элемент всегда видимым
      isVisible.value = true;
    }
  };
  
  /**
   * Остановить наблюдение
   * Requirement 7.4
   */
  const disconnect = () => {
    if (observer) {
      observer.disconnect();
      observer = null;
      log('Disconnected observer');
    }
    
    // Возобновляем все анимации при отключении
    if (pausedAnimations.size > 0) {
      resumeAnimations();
    }
    
    observedElement = null;
  };
  
  /**
   * Полная очистка ресурсов
   */
  const cleanup = () => {
    disconnect();
    pausedAnimations.clear();
    log('Cleanup completed');
  };
  
  // Автоматическая очистка при unmount (Requirement 7.4)
  onUnmounted(() => {
    cleanup();
  });
  
  return {
    isVisible,
    observe,
    disconnect,
    pauseAnimations,
    resumeAnimations,
    isSupported,
    cleanup
  };
}
